import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { CRMNote } from "@/types";

interface UseNoteDeepLinkOptions {
  dataReady: boolean;
  expandParam?: string;
  highlightParam?: string;
  openParam?: string;
}

interface UseNoteDeepLinkResult {
  expandRowId: string | null;
  highlightNoteId: string | null;
  openNoteId: string | null;
  tableRef: React.RefObject<HTMLDivElement>;
  getCopyLink: (note: CRMNote) => string;
  setOpenNoteInUrl: (noteId: string | null) => void;
}

/**
 * Manages the notes-table deep-link URL contract:
 *
 * - `expandNotes` and `highlightNote` are one-shot triggers stripped from the
 *   URL on arrival once `dataReady` is true.
 * - `openNote` is a live URL-backed value. It is preserved in the URL while
 *   the side sheet is open so users can copy the browser URL to share the
 *   currently open note. Callers sync it via `setOpenNoteInUrl` on open/close.
 * - `openNote` implies highlight and wins over `highlightNote` when both are
 *   present.
 *
 * Row-level scroll for highlightNote is handled by NotesTableBase after data loads.
 */
export function useNoteDeepLink({
  dataReady,
  expandParam = "expandNotes",
  highlightParam = "highlightNote",
  openParam = "openNote"
}: UseNoteDeepLinkOptions): UseNoteDeepLinkResult {
  const [searchParams] = useSearchParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [expandRowId, setExpandRowId] = useState<string | null>(null);
  const [highlightNoteId, setHighlightNoteId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const openNote = searchParams.get(openParam);
  const openNoteId = dataReady ? openNote : null;

  const getCopyLink = useCallback(
    (note: CRMNote) => `${window.location.origin}${pathname}?${openParam}=${encodeURIComponent(note.id)}`,
    [pathname, openParam]
  );

  const setOpenNoteInUrl = useCallback(
    (noteId: string | null) => {
      const next = new URLSearchParams(search);
      if (noteId) {
        next.set(openParam, noteId);
      } else {
        next.delete(openParam);
      }
      const nextSearch = next.toString();
      const currentSearch = search.startsWith("?") ? search.slice(1) : search;
      if (nextSearch === currentSearch) return;
      navigate({ pathname, search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    },
    [navigate, openParam, pathname, search]
  );

  useEffect(() => {
    const expandValue = searchParams.get(expandParam);
    const highlightNote = searchParams.get(highlightParam);

    if ((expandValue || highlightNote) && dataReady) {
      const next = new URLSearchParams(searchParams);
      next.delete(expandParam);
      next.delete(highlightParam);
      navigate({ pathname, search: next.toString() ? `?${next.toString()}` : "" }, { replace: true });

      if (expandValue) setExpandRowId(expandValue);
      if (highlightNote && !openNote) setHighlightNoteId(highlightNote);

      if (expandValue && tableRef.current) {
        tableRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest"
        });
      }
    }
  }, [dataReady, expandParam, highlightParam, navigate, openNote, pathname, searchParams]);

  useEffect(() => {
    if (openNote && dataReady) setHighlightNoteId(openNote);
  }, [openNote, dataReady]);

  return { expandRowId, highlightNoteId, openNoteId, tableRef, getCopyLink, setOpenNoteInUrl };
}
